import React, { useState, useEffect } from "react";
import AsyncSelect from "react-select/async";
import { useSettings } from "@peracto/peracto-hooks";

import debounce from "debounce-promise";
import axios from "axios";

import { useConfig } from "@peracto/peracto-config";

const MenuItemsForm = ({
    onChange,
    state: { category = {}, menu = {}, parentCategory = {}, menuItem = {} },
}) => {
    const [parentMenu, setParentMenu] = useState(menu);

    const { values: p_values } = useSettings();

    // START - Menu Items
    const configCategoryPath =
        p_values?.sub_urls?.sitemap_categories_sub_url || "category";

    useEffect(() => {
        onChange({ categoryPath: configCategoryPath });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [configCategoryPath]);

    const config = useConfig();
    const API_URL = config.get("api");

    const fetchMenus = async (input) => {
        const { data } = await axios.get(
            `${API_URL}/menus?name=${input || ""}`,
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            },
        );

        const parsedData = data["hydra:member"];

        const values = parsedData.map((menu) => ({
            label: menu.name,
            value: `/menus/${menu.code}`,
        }));

        return values;
    };

    const debouncedFetchMenus = debounce(fetchMenus, 200);

    let menuItems = [];

    const formatMenuItems = (menu) => {
        menu.forEach((item) => {
            menuItems.push({
                label: item.linkText,
                value: {
                    path: item.path,
                    title: item.linkText,
                    parent: item?.parent || null,
                },
            });

            if (item?.children?.length > 0) {
                item.children.forEach((child) => {
                    child.parent = {
                        path: item.path,
                        linkText: item.linkText,
                    };
                });
                formatMenuItems(item.children);
            }
        });
        return menuItems;
    };

    const fetchMenuItems = async (input) => {
        menuItems = [];

        const { data } = await axios.get(`${API_URL}${parentMenu.value}`, {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
        });

        const values = formatMenuItems(data.menuItems);

        return values.filter((val) =>
            val.label.toLowerCase().includes(input.toLowerCase()),
        );
    };

    const debouncedFetchMenuItems = debounce(fetchMenuItems, 200);

    // END - Menu Items

    // START - Categories
    const fetchCategories = async (input) => {
        const { data } = await axios.get(
            `${API_URL}/categories?heading=${input}`,
            {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
            },
        );

        const values = formatCategories(data["hydra:member"]);

        return values;
    };

    const debouncedFetchCategories = debounce(fetchCategories, 200);

    const formatCategories = (categories) => {
        return categories.map((category) => ({
            label: category.heading,
            value: {
                id: category.id,
                heading: category.heading,
                description: category.description,
                image: category?.resources?.[0]?.path,
            },
        }));
    };

    // END - Categories
    return (
        <>
            <div className="form-group">
                <label>Category</label>
                <AsyncSelect
                    className="w-100"
                    loadOptions={(input) => debouncedFetchCategories(input)}
                    isSearchable={true}
                    value={category}
                    onChange={(option) => {
                        onChange({ category: option });
                        onChange({ categoryPath: option.value });
                    }}
                    placeholder="Select a category..."
                    classNamePrefix="peracto-select"
                    noOptionsMessage={({ inputValue }) => {
                        if (inputValue.length > 0) {
                            return `No categories found for '${inputValue}'.`;
                        } else {
                            return "Enter text to begin searching.";
                        }
                    }}
                />
            </div>

            <div className="form-group">
                <label>Menu</label>
                <AsyncSelect
                    className="w-100"
                    loadOptions={(input) => debouncedFetchMenus(input)}
                    isSearchable={true}
                    defaultOptions={true}
                    value={menu}
                    onChange={(option) => {
                        setParentMenu(option);
                        onChange({ menu: option });
                        onChange({ menuItem: {} });
                    }}
                    placeholder="Select a menu..."
                    classNamePrefix="peracto-select"
                    noOptionsMessage={({ inputValue }) => {
                        if (inputValue.length > 0) {
                            return `No menus found for '${inputValue}'.`;
                        } else {
                            return "Enter text to begin searching.";
                        }
                    }}
                />
            </div>

            <div className="form-group">
                <label>Menu Item</label>
                <AsyncSelect
                    className="w-100"
                    loadOptions={(input) => debouncedFetchMenuItems(input)}
                    isSearchable={true}
                    value={menuItem}
                    onChange={(option) => {
                        onChange({ menuItem: option });
                        onChange({ menuItemPath: option.value.path });
                    }}
                    isDisabled={!parentMenu}
                    placeholder="Select a menu item..."
                    classNamePrefix="peracto-select"
                    noOptionsMessage={({ inputValue }) => {
                        if (inputValue.length > 0) {
                            return `No menu items found for '${inputValue}'.`;
                        } else {
                            return "Enter text to begin searching.";
                        }
                    }}
                />
            </div>
        </>
    );
};

export default MenuItemsForm;
